import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, UserRound, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function UserManagementPage() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ email: "", password: "", full_name: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/users")
            .then((r) => setUsers(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

    const addSalesperson = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/admin/users", form);
            toast.success(`Added ${form.full_name}`);
            setForm({ email: "", password: "", full_name: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add user");
        } finally { setSaving(false); }
    };

    const removeUser = async (u) => {
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(`Removed ${u.full_name}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to remove");
        }
    };

    const admins = users.filter((u) => u.role === "admin");
    const salespeople = users.filter((u) => u.role === "salesperson");

    return (
        <div className="p-6 lg:p-10 max-w-5xl">
            <div className="mb-8 fade-in-up">
                <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Team</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A4331] mt-2">Salespeople</h1>
                <p className="text-[#475569] mt-3 max-w-2xl">
                    Add or remove sales team members. Removing a user also clears any assignments allocated to them.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Add form */}
                <Card className="lg:col-span-1 p-6 bg-white border-[#E2E8F0] fade-in-up stagger-1" data-testid="add-salesperson-card">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-9 h-9 rounded-md bg-[#1A4331] flex items-center justify-center">
                            <UserPlus className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="font-heading text-lg font-medium text-[#1A4331]">Add salesperson</h2>
                    </div>
                    <form onSubmit={addSalesperson} className="space-y-4" data-testid="add-salesperson-form">
                        <div>
                            <Label className="text-sm">Full name</Label>
                            <Input required value={form.full_name}
                                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                className="mt-1 h-10 bg-white border-[#E2E8F0]"
                                placeholder="Alex Jordan"
                                data-testid="new-name-input" />
                        </div>
                        <div>
                            <Label className="text-sm">Email</Label>
                            <Input type="email" required value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-1 h-10 bg-white border-[#E2E8F0]"
                                placeholder="alex.jordan@rcnpharma.co.uk"
                                data-testid="new-email-input" />
                        </div>
                        <div>
                            <Label className="text-sm">Temporary password</Label>
                            <Input type="text" required minLength={6} value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="mt-1 h-10 bg-white border-[#E2E8F0] font-mono"
                                placeholder="min 6 chars"
                                data-testid="new-password-input" />
                        </div>
                        <Button type="submit" disabled={saving} className="w-full bg-[#1A4331] hover:bg-[#123022] text-white gap-2" data-testid="add-salesperson-submit">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Add to team
                        </Button>
                    </form>
                </Card>

                {/* Team list */}
                <div className="lg:col-span-2 space-y-4">
                    {loading ? (
                        <Card className="p-10 bg-white border-[#E2E8F0] text-center text-[#8A9A86]">Loading…</Card>
                    ) : (
                        <>
                            <div>
                                <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">Administrators</p>
                                <div className="space-y-2">
                                    {admins.map((u) => (
                                        <Card key={u.id} className="p-4 bg-white border-[#E2E8F0] flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 text-[#1A4331] flex items-center justify-center">
                                                <Shield className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-[#1E293B]">{u.full_name}</p>
                                                <p className="text-xs text-[#475569]">{u.email}</p>
                                            </div>
                                            <Badge className="bg-[#1A4331] text-white hover:bg-[#1A4331] border-0">Admin</Badge>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">
                                    Salespeople ({salespeople.length})
                                </p>
                                <div className="space-y-2" data-testid="salespeople-list">
                                    {salespeople.length === 0 && (
                                        <Card className="p-8 bg-white border-[#E2E8F0] text-center text-[#8A9A86] text-sm">
                                            No salespeople yet. Add one using the form.
                                        </Card>
                                    )}
                                    {salespeople.map((u) => (
                                        <Card key={u.id} className="p-4 bg-white border-[#E2E8F0] flex items-center gap-3 card-lift" data-testid={`sp-row-${u.id}`}>
                                            <div className="w-10 h-10 rounded-full bg-[#F1F5F9] text-[#1A4331] flex items-center justify-center">
                                                <UserRound className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-[#1E293B]">{u.full_name}</p>
                                                <p className="text-xs text-[#475569]">{u.email}</p>
                                            </div>
                                            <p className="text-xs text-[#8A9A86] hidden sm:block">
                                                Joined {new Date(u.created_at).toLocaleDateString("en-GB")}
                                            </p>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button variant="ghost" size="sm" className="text-[#991B1B] hover:text-[#991B1B] hover:bg-red-50" data-testid={`delete-${u.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Remove {u.full_name}?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This will remove the salesperson account and clear any drug assignments allocated to them. This action cannot be undone.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel data-testid={`cancel-delete-${u.id}`}>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => removeUser(u)} className="bg-[#991B1B] hover:bg-[#7f1818]" data-testid={`confirm-delete-${u.id}`}>
                                                            Remove
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
